import React, { useContext, useState } from 'react';
import AuthContext from '../shared/AuthContext';
import { isSystemAdmin } from '../../utils/platformRoles';
import { Navigate } from 'react-router-dom';
import './AdminAdministratorControlsPage.css';

function apiError(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.detail || data.message || data.description || fallback;
}

export default function AdminAdministratorControlsPage() {
  const { apiClient, user } = useContext(AuthContext);
  const profile = user || readProfileSafe();
  const [email, setEmail] = useState('');
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [paidBlocked, setPaidBlocked] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [overridePlan, setOverridePlan] = useState('');
  const [storedPlan, setStoredPlan] = useState('FREE');
  const [grantPlatformAdmin, setGrantPlatformAdmin] = useState(false);
  const [grantSystemAdmin, setGrantSystemAdmin] = useState(false);

  if (!isSystemAdmin(profile)) {
    return <Navigate to="/admin" replace />;
  }

  const lookup = async (e) => {
    e?.preventDefault();
    const q = email.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await apiClient.get('/api/admin/system/users/lookup', { params: { email: q } });
      const u = res.data;
      setTarget(u);
      setPaidBlocked(Boolean(u.paidPlanBlocked));
      setSuspended(Boolean(u.accountSuspended));
      setOverridePlan(u.adminPlanOverride || '');
      setStoredPlan(u.storedSubscriptionPlan || 'FREE');
      setGrantPlatformAdmin(Boolean(u.platformAdmin));
      setGrantSystemAdmin(Boolean(u.systemAdmin));
    } catch (err) {
      setTarget(null);
      setError(apiError(err, 'User not found or lookup failed.'));
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!target?.userId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const body = {
        paidPlanBlocked: paidBlocked,
        accountSuspended: suspended,
        subscriptionPlan: storedPlan,
        platformAdmin: grantPlatformAdmin,
        systemAdmin: grantSystemAdmin,
      };
      if (overridePlan === '' || overridePlan === 'CLEAR') {
        body.adminPlanOverride = 'CLEAR';
      } else {
        body.adminPlanOverride = overridePlan;
      }
      const res = await apiClient.patch(
        `/api/admin/system/users/${target.userId}/entitlements`,
        body,
      );
      setTarget(res.data);
      setMessage('Entitlements updated. Ask the user to refresh the app (or wait ~30s) to pick up the new plan.');
    } catch (err) {
      setError(apiError(err, 'Could not save changes.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-sys-page">
      <header className="admin-sys-head">
        <h1>Administrator controls</h1>
        <p className="admin-sys-sub">
          System admin tools — block paid plans, override entitlements, suspend accounts, and manage admin roles.
        </p>
      </header>

      <form className="admin-sys-lookup" onSubmit={lookup}>
        <label className="admin-sys-label">
          User email
          <input
            type="email"
            className="admin-sys-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
          />
        </label>
        <button type="submit" className="home-btn home-btn-primary" disabled={loading}>
          {loading ? 'Looking up…' : 'Find user'}
        </button>
      </form>

      {error && <p className="admin-sys-error" role="alert">{error}</p>}
      {message && <p className="admin-sys-success" role="status">{message}</p>}

      {target && (
        <section className="admin-sys-panel">
          <h2>{target.fullName || target.email}</h2>
          <dl className="admin-sys-stats">
            <div><dt>Email</dt><dd>{target.email}</dd></div>
            <div><dt>Stored plan</dt><dd>{target.storedSubscriptionPlan || 'FREE'}</dd></div>
            <div><dt>Effective plan</dt><dd>{target.effectivePlan}</dd></div>
            <div><dt>Plan override</dt><dd>{target.adminPlanOverride || 'None'}</dd></div>
            <div><dt>Paid blocked</dt><dd>{target.paidPlanBlocked ? 'Yes' : 'No'}</dd></div>
            <div><dt>Stripe</dt><dd>{target.stripeLinked ? 'Linked' : 'None'}</dd></div>
          </dl>

          {(target.paidPlanBlocked
            || (target.adminPlanOverride && target.adminPlanOverride !== target.storedSubscriptionPlan)
            || (target.effectivePlan !== storedPlan && storedPlan !== 'FREE')) && (
            <p className="admin-sys-hint" role="status">
              Features use the <strong>effective plan</strong>. Uncheck paid block, clear a &quot;Force FREE&quot; override,
              or use Grant Pro / Grant Team below. The user may need to refresh the app after you save.
            </p>
          )}

          <div className="admin-sys-quick">
            <button
              type="button"
              className="home-btn home-btn-secondary"
              onClick={() => {
                setPaidBlocked(false);
                setOverridePlan('');
                setStoredPlan('PRO');
              }}
            >
              Grant Pro access
            </button>
            <button
              type="button"
              className="home-btn home-btn-secondary"
              onClick={() => {
                setPaidBlocked(false);
                setOverridePlan('');
                setStoredPlan('TEAM');
              }}
            >
              Grant Team access
            </button>
          </div>

          <div className="admin-sys-controls">
            <label className="admin-sys-check">
              <input
                type="checkbox"
                checked={paidBlocked}
                onChange={(e) => setPaidBlocked(e.target.checked)}
              />
              Block PRO &amp; TEAM (force FREE entitlements)
            </label>
            <label className="admin-sys-check">
              <input
                type="checkbox"
                checked={suspended}
                onChange={(e) => setSuspended(e.target.checked)}
              />
              Suspend account (blocks sign-in)
            </label>

            <label className="admin-sys-label">
              Admin plan override
              <select
                className="admin-sys-input"
                value={overridePlan}
                onChange={(e) => setOverridePlan(e.target.value)}
              >
                <option value="">None (use billing + flags)</option>
                <option value="FREE">Force FREE</option>
                <option value="PRO">Force PRO</option>
                <option value="TEAM">Force TEAM</option>
                <option value="CLEAR">Clear override</option>
              </select>
            </label>

            <label className="admin-sys-label">
              Stored subscription plan (database)
              <select
                className="admin-sys-input"
                value={storedPlan}
                onChange={(e) => setStoredPlan(e.target.value)}
              >
                <option value="FREE">FREE</option>
                <option value="PRO">PRO</option>
                <option value="TEAM">TEAM</option>
              </select>
            </label>

            <fieldset className="admin-sys-fieldset">
              <legend>Roles</legend>
              <label className="admin-sys-check">
                <input
                  type="checkbox"
                  checked={grantPlatformAdmin}
                  onChange={(e) => setGrantPlatformAdmin(e.target.checked)}
                />
                Platform admin (ROLE_ADMIN — console access)
              </label>
              <label className="admin-sys-check">
                <input
                  type="checkbox"
                  checked={grantSystemAdmin}
                  onChange={(e) => setGrantSystemAdmin(e.target.checked)}
                />
                System admin (ROLE_SYSTEM_ADMIN — this page)
              </label>
            </fieldset>
          </div>

          <button
            type="button"
            className="home-btn home-btn-primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </section>
      )}
    </div>
  );
}

function readProfileSafe() {
  try {
    return JSON.parse(localStorage.getItem('userProfile') || 'null');
  } catch {
    return null;
  }
}
